import { useCallback } from 'react';
import { toast } from 'sonner';
import type { StageData, Workflow } from '@/components/ApprovalStageWizard/types';
import type { UpdateWorkflowRequest } from '@/types/workflow';
import { ApiError } from '@/services/apiClient';
import * as workflowService from '@/services/workflowService';
import { mapApproverTypeToBackend, mapRoleTypeToBackend } from '../utils';

export interface UseWorkflowStageEditorParams {
  isLive: boolean;
  selectedWorkflowId: string | null;
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
  loadWorkflows: () => Promise<void>;
}

export interface UseWorkflowStageEditorResult {
  handleUpdateStage: (stageId: string, stageData: StageData) => Promise<void>;
  handleSaveDraft: (stageId: string, stageData: StageData) => Promise<void>;
}

/**
 * Builds the backend update request for a workflow where a single stage is being
 * edited, using the in-flight stageData for that stage.
 */
const buildStageEditRequest = (
  updatedWorkflow: Workflow,
  stageId: string,
  stageData: StageData
): UpdateWorkflowRequest => ({
  name: updatedWorkflow.name,
  description: updatedWorkflow.description,
  isActive: updatedWorkflow.status === 'active',
  isDefault: updatedWorkflow.isDefault ?? false,
  stages: updatedWorkflow.stages.map((stage) => {
    const isUpdatedStage = stage.id === stageId;
    const stageDataToUse = isUpdatedStage ? stageData : stage.stageData;

    // Map approver from stage data
    const approverType = stageDataToUse?.approver
      ? mapApproverTypeToBackend(stageDataToUse.approver.approverType)
      : 'manager' as const;

    const approverObj: {
      approverType: 'specific' | 'role' | 'manager' | 'department_head' | 'custom';
      approverId?: string;
      approverRole?: string;
      approverOrder: number;
    } = {
      approverType,
      approverOrder: 1,
    };

    if (stageDataToUse?.approver) {
      if (stageDataToUse.approver.specificUserId) {
        approverObj.approverId = String(stageDataToUse.approver.specificUserId);
      }
      if (stageDataToUse.approver.roleType) {
        approverObj.approverRole = mapRoleTypeToBackend(stageDataToUse.approver.roleType);
      }
    }

    // Use undefined for temporary stage IDs so backend generates proper GUIDs
    const backendStageId = (stage.id.startsWith('temp-') || stage.id.startsWith('placeholder-')) ? undefined : stage.id;

    // Build advanced config JSON from stageData
    const advancedConfig = stageDataToUse ? {
      smartRules: {
        rules: stageDataToUse.smartRules.rules,
        onApproved: stageDataToUse.smartRules.onApproved,
        onRejected: stageDataToUse.smartRules.onRejected,
        timeout: stageDataToUse.smartRules.timeout,
      },
      notifications: stageDataToUse.notifications,
    } : undefined;

    return {
      id: backendStageId,
      name: isUpdatedStage ? stageData.basicDetails.stageName : stage.name,
      description: isUpdatedStage ? stageData.basicDetails.description : stage.description,
      stageOrder: stage.order,
      approvalType: 'any' as const,
      requiredApprovals: 1,
      allowApproverEdit: stageDataToUse?.allowApproverEdit ?? false,
      advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
      approvers: [{
        ...approverObj,
        cannotApproveOwn: stageDataToUse?.approver?.cannotApproveOwn ?? true,
        ruleConfigJson: stageDataToUse?.approver?.ruleDetermined
          ? JSON.stringify(stageDataToUse.approver.ruleDetermined)
          : undefined,
      }],
    };
  }),
});

const hasTempStages = (workflow: Workflow): boolean =>
  workflow.stages.some((s) => s.id.startsWith('temp-') || s.id.startsWith('placeholder-'));

/**
 * Owns saving a single stage's configuration, either as a completed
 * configuration or as a draft.
 */
export const useWorkflowStageEditor = ({
  isLive,
  selectedWorkflowId,
  workflows,
  setWorkflows,
  loadWorkflows,
}: UseWorkflowStageEditorParams): UseWorkflowStageEditorResult => {
  const handleUpdateStage = useCallback(async (stageId: string, stageData: StageData) => {
    if (!selectedWorkflowId) return;

    // Get the current workflow BEFORE updating state to avoid stale closure issues
    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    // Build the updated workflow object
    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: currentWorkflow.stages.map((stage) => {
        if (stage.id !== stageId) return stage;

        // Count rules
        const preApprovalRules = stageData.smartRules.rules.filter(
          (r) => r.executionTiming === 'pre-approval'
        ).length;
        const postApprovalRules = stageData.smartRules.rules.filter(
          (r) => r.executionTiming === 'post-approval'
        ).length;

        return {
          ...stage,
          name: stageData.basicDetails.stageName,
          description: stageData.basicDetails.description,
          status: 'configured' as const,
          stageData,
          ruleCount: {
            preApproval: preApprovalRules,
            postApproval: postApprovalRules,
          },
        };
      }),
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend in live mode
    if (isLive) {
      try {
        // Build update request using the updated workflow object (not stale closure)
        await workflowService.updateWorkflow(
          selectedWorkflowId,
          buildStageEditRequest(updatedWorkflow, stageId, stageData)
        );
        toast.success('Stage configuration saved!');

        // Reload workflow to get canonical IDs from backend (especially for new stages)
        if (hasTempStages(updatedWorkflow)) {
          await loadWorkflows();
        }
      } catch (err) {
        console.error('Failed to update workflow:', err);
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error('Failed to save stage configuration to server');
        }
        // Revert local state on error
        loadWorkflows();
      }
    } else {
      toast.success('Stage configuration saved!');
    }
  }, [isLive, selectedWorkflowId, workflows, setWorkflows, loadWorkflows]);

  // Handle saving draft to backend (without closing wizard)
  const handleSaveDraft = useCallback(async (stageId: string, stageData: StageData) => {
    if (!selectedWorkflowId) return;

    // Get the current workflow
    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    // Build the updated workflow object (similar to handleUpdateStage but without marking as configured)
    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: currentWorkflow.stages.map((stage) => {
        if (stage.id !== stageId) return stage;

        return {
          ...stage,
          name: stageData.basicDetails.stageName,
          description: stageData.basicDetails.description,
          // Keep status as draft when saving draft
          status: stage.status === 'configured' ? 'configured' : 'draft' as const,
          stageData,
        };
      }),
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend in live mode
    if (isLive) {
      try {
        await workflowService.updateWorkflow(
          selectedWorkflowId,
          buildStageEditRequest(updatedWorkflow, stageId, stageData)
        );

        // Reload workflow to get canonical IDs from backend (especially for new stages)
        if (hasTempStages(updatedWorkflow)) {
          await loadWorkflows();
        }
      } catch (err) {
        console.error('Failed to save draft:', err);
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error('Failed to save draft to server');
        }
        // Revert local state on error
        loadWorkflows();
        throw err;
      }
    }
  }, [isLive, selectedWorkflowId, workflows, setWorkflows, loadWorkflows]);

  return { handleUpdateStage, handleSaveDraft };
};
